import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

export class SMMValidators {
    /**
     * Checks if value of control is a valid phone number.
     */
    static phoneNumber(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const controlValue = control.value;
            if (!controlValue) {
                return null;
            }
            const value = controlValue.replace(/\s/g, '');
            const hasCountryCode = /^(\+|(00))\d{2,}$/.test(value);
            //uncomment to force romanian mobile
            //const format = /^(\+|(00))407\d+$/.test(value);
            const format = true;
            let lengthOK  = false;
            if(value.startsWith('+40') || value.startsWith('0040')){
                lengthOK  = value.startsWith('+') ? (value.length == 12) : (value.length == 13);
            }else{
                lengthOK  = value.length > 7;
            }

            const phoneNumberValid = hasCountryCode && lengthOK;
            return !phoneNumberValid ? {
                phoneNumberValidation: {
                    invalid: !phoneNumberValid,
                    countryCode: !hasCountryCode,
                    length: !lengthOK,
                    format: !format
                }
            } : null;
        }
    }

    static validateRomanianCIF(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const v = control.value;
            if (!v) {
                return null;
            }
            let cif:string = v.toString();
            let notAcif = false;

            // begin testing:
            const testKey = '753217532'
            const controlNumber = parseInt(cif.substring(cif.length - 1));
            // remove control number:
            cif = cif.substring(0, cif.length - 1);
            // pad left with zeros to reach testKey length:
            while (cif.length !== testKey.length) {
                cif = '0' + cif;
            }
            let sum = 0;
            let i = cif.length;

            while (i--) {
                sum = sum + (parseInt(cif.charAt(i)) * parseInt(testKey.charAt(i)));
            }

            let calculatedControlNumber = sum * 10 % 11;

            if (calculatedControlNumber === 10) {
                calculatedControlNumber = 0;
            }
            notAcif = (controlNumber !== calculatedControlNumber);
            return notAcif ? {
                cifInvalid: true
            } : null;
        }
    }

    static passwordValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) {
                return null;
            }
            const longEnough = value.length >= 10;
            const shortEnough = value.length < 100;
            const hasNumbers =  /\d/.test(value);
            const hasUpperCase = /[A-Z]/.test(value);
            const hasLowerCase = /[a-z]/.test(value);
            const hasSpecialChars = /[^A-Za-z0-9]/.test(value);

            if(!longEnough)   { /*sorry mate*/ return {tooShort: true}; }
            if(!shortEnough)  { /*congratulations mate*/ return {tooLong: true}; }
            if(!hasNumbers)   { return {noNumbers: true}; }
            if(!hasUpperCase) { return {noUpperCase: true}; }
            if(!hasLowerCase) { return {noLowerCase: true}; }
            if(!hasSpecialChars) { return {noSpecialCharacter: true}; }
            return null;
        }
    }

    static cnpValidator(): ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const cnp = control.value;

            if(!cnp || cnp == "" ){
                return null;
            }

            if (!cnp || cnp.length !== 13 || !/^\d{13}$/.test(cnp)) {
                return { invalidCNP: 'CNP-ul trebuie să conțină exact 13 cifre.' };
            }

            const checkSumWeights = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
            const checkDigit = parseInt(cnp[12], 10);
            let sum = 0;

            for (let i = 0; i < 12; i++) {
                sum += parseInt(cnp[i], 10) * checkSumWeights[i];
            }

            const calculatedCheckDigit = sum % 11 === 10 ? 1 : sum % 11;

            return calculatedCheckDigit === checkDigit ? null : { invalidCNP: 'CNP-ul introdus nu este valid.' };
        }
    }

    static smellsLikeIban():ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            const smelsLike:boolean = /^([A-Z]{2}[ \-]?[0-9]{2})(?=(?:[ \-]?[A-Z0-9]){9,30}$)((?:[ \-]?[A-Z0-9]{3,5}){2,7})([ \-]?[A-Z0-9]{1,3})?$/.test(value);
            return smelsLike ? null : { invalidIbanFormat: 'IBAN-ul introdus nu are formatul potrivit.'};
        }
    }

    static validateIbanChecksum():ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            const ibanStripped = value.replace(/[^A-Z0-9]+/gi,'') //keep numbers and letters only
                .toUpperCase(); //calculation expects upper-case
            const m = ibanStripped.match(/^([A-Z]{2})([0-9]{2})([A-Z0-9]{9,30})$/);
            if(!m) return { invalidIbanCheckSum: 'IBAN-ul introdus nu este incorect.'};

            const numbericed = (m[3] + m[1] + m[2]).replace(/[A-Z]/g,function(ch){
                //replace upper-case characters by numbers 10 to 35
                return (ch.charCodeAt(0)-55);
            });
            //The resulting number would be to long for javascript to handle without loosing precision.
            //So the trick is to chop the string up in smaller parts.
            const mod97 = numbericed.match(/\d{1,7}/g)
                .reduce(function(total, curr){ return Number(total + curr)%97},'');

            const valid:boolean = (mod97 === 1);
            return valid ? null : { invalidIbanCheckSum: 'IBAN-ul introdus nu este incorect.'};
        }
    };
}
